import { TagManager } from 'sih-gtm/build/TagManager'

import { CheckoutUiStatusGapInterface } from '@lib/GTMBase'
import * as Sentry from '@sentry/browser'
import { CheckOutFlowTypeEnum } from '@uctypes/api/globalTypes'
import { checkoutLogger } from '@utility/Logger'

const SESSION_DEDUPE_PREFIX = 'checkout_ui_gap_reported'

export type CheckoutUiStatusGapReason =
  | 'status_missing_on_plans_mount'
  | 'hydrate_fallback_timeout'
  | 'renderer_effect_without_status'

export interface CheckoutUiStatusGapContext {
  surface: string
  reason: CheckoutUiStatusGapReason
  sessionFlowType?: string | null
  stateFlowType?: CheckOutFlowTypeEnum | string | null
  hasCurrentUser?: boolean
  hasCheckoutStatusId?: boolean
  isGuest?: boolean
  isHydrated?: boolean
  isCheckoutStatusLoading?: boolean
}

function pathname(): string {
  if (typeof window === 'undefined') {
    return ''
  }
  return window.location.pathname
}

function shouldDedupe(reason: CheckoutUiStatusGapReason): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false
  }
  const key = `${SESSION_DEDUPE_PREFIX}:${reason}`
  if (window.sessionStorage.getItem(key)) {
    return true
  }
  window.sessionStorage.setItem(key, '1')
  return false
}

function pushGtmEvent(context: CheckoutUiStatusGapContext): void {
  const eventData: CheckoutUiStatusGapInterface = {
    event: 'checkout_ui_status_gap',
    eventParams: {
      surface: context.surface,
      reason: context.reason,
      pathname: pathname(),
      sessionFlowType: context.sessionFlowType ?? null,
      stateFlowType: context.stateFlowType ?? null,
      hasCurrentUser: context.hasCurrentUser ?? false,
      hasCheckoutStatusId: context.hasCheckoutStatusId ?? false,
      isGuest: context.isGuest ?? false,
      isHydrated: context.isHydrated ?? false,
      isCheckoutStatusLoading: context.isCheckoutStatusLoading ?? false,
    },
  }
  try {
    TagManager.pushEvent(eventData)
  } catch {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'checkout_ui_status_gap',
        ...eventData.eventParams,
      })
    }
  }
}

/**
 * Reports checkout UI gaps to GTM (GA4) and Sentry for production RCA.
 * Deduped per session + reason to limit noise.
 */
export function reportCheckoutUiStatusGap(context: CheckoutUiStatusGapContext): void {
  if (shouldDedupe(context.reason)) {
    return
  }

  checkoutLogger.warn('[CHECKOUT][UI_STATUS_GAP]', context)

  pushGtmEvent(context)

  Sentry.captureMessage(`checkout_ui_status_gap: ${context.reason}`, {
    level: 'warning',
    tags: {
      surface: context.surface,
      reason: context.reason,
    },
    extra: {
      pathname: pathname(),
      sessionFlowType: context.sessionFlowType,
      stateFlowType: context.stateFlowType,
      hasCurrentUser: context.hasCurrentUser,
      hasCheckoutStatusId: context.hasCheckoutStatusId,
      isGuest: context.isGuest,
      isHydrated: context.isHydrated,
      isCheckoutStatusLoading: context.isCheckoutStatusLoading,
    },
  })
}
